import { useMemo } from "react";
import {
	dateFnsLocale,
	formatProfileCalendarDate,
	formatProfileCalendarDateFromInput,
	formatProfileDate,
	formatProfileDateRange,
	formatProfileDateTime,
	formatProfileRelative,
	formatProfileTime,
	normalizeProfileDateFormat,
} from "@/lib/account/format-profile-date-time";
import { browserTimeZone, type ProfileSettings, parseProfileSettings } from "@/lib/account/profile-types";
import stores from "@/lib/stores/web-api-stores";

export interface ProfileFormat {
	/** Calendar date in the profile pattern. */
	readonly date: (date: Date) => string;
	readonly dateFromInput: (value: string | Date | number | null | undefined, fallback?: string) => string;
	readonly dateRange: (from: Date, to: Date) => string;
	readonly dateTime: (date: Date) => string;
	/** date-fns locale of the profile language, for calendars. */
	readonly locale: ReturnType<typeof dateFnsLocale>;
	/** Weekday + date in the profile pattern. */
	readonly dateWithWeekday: (date: Date) => string;
	/** Validated date-fns pattern, e.g. `dd.MM.yyyy`. */
	readonly pattern: string;
	/** "3 minutes ago" in the profile language. */
	readonly relative: (date: Date) => string;
	readonly settings: ProfileSettings;
	readonly time: (date: Date) => string;
}

/** Display settings: profile pattern, 12/24h, language. Zone stays the browser zone (matches calendar day grouping). */
export const toProfileDisplaySettings = (rawUser: unknown): ProfileSettings => ({
	...parseProfileSettings(rawUser),
	timeZone: browserTimeZone(),
});

export const buildProfileFormat = (settings: ProfileSettings): ProfileFormat => ({
	settings,
	pattern: normalizeProfileDateFormat(settings.dateFormat),
	date: (date) => formatProfileCalendarDate(date, settings),
	dateFromInput: (value, fallback) => formatProfileCalendarDateFromInput(value, settings, fallback),
	dateRange: (from, to) => formatProfileDateRange(from, to, settings),
	dateTime: (date) => formatProfileDateTime(date, settings),
	locale: dateFnsLocale(settings.languageSetting),
	dateWithWeekday: (date) => formatProfileDate(date, settings),
	relative: (date) => formatProfileRelative(date, settings),
	time: (date) => formatProfileTime(date, settings),
});

/** Snapshot for plain helpers that cannot call hooks. Not reactive: re-read on each render of the caller. */
export const getProfileFormat = (): ProfileFormat =>
	buildProfileFormat(toProfileDisplaySettings(stores.user.useStore.getState().value));

/** Reads the store directly (no query observer) so it is cheap inside rows and cells. */
export const useProfileFormat = (): ProfileFormat => {
	const rawUser = stores.user.useStore((state) => state.value);
	return useMemo(() => buildProfileFormat(toProfileDisplaySettings(rawUser)), [rawUser]);
};
